import React, { FC, useEffect, useMemo, useState } from 'react';

import { observer } from 'mobx-react';
import classNames from 'classnames';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import Modal, {
    DynamicStyles,
    InitModalStyle,
    MODAL_DISABLE_MIN_HEIGHT_CLASS,
    MODAL_NO_PADDINGS_CLASS,
    ModalHeader,
} from '@/components/Common/Modal';
import { Input } from '@/components/Common/UIKit';
import MissingPhoneInput from '@/components/Common/MissingDetails/MissingPhoneInput';
import MissingPasswordInput from '@/components/Common/MissingDetails/MissingPasswordInput';
import EditableCellPopup from '@/components/Common/EditableCellPopup';
import appConfig from '@/config/env';
import useRecipientDetails from '@/components/Common/hooks/useRecipientDetails';
import { useStores } from '@/components/hooks';
import type { CustomGroup } from '@/stores';

import styles from './GroupDetailsModal.module.scss';

const nameSpace = 'sharingBlock.groupPhonesModal';
const policyStepNameSpace = 'uploadFiles.policyStep';

const MODAL_DYNAMIC_STYLES: DynamicStyles = {
    width: true,
    wrapClassName: true,
};

const INIT_STYLE: InitModalStyle = {
    additionalWrapClassName: classNames(MODAL_DISABLE_MIN_HEIGHT_CLASS, MODAL_NO_PADDINGS_CLASS),
    initWidth: 600,
};

const PHONE_LAST_DIGITS = -2;

interface MemberRowProps {
    email: string;
    showPhones: boolean;
    showPasswords: boolean;
    isMissing: boolean;
}

const MemberRow: FC<MemberRowProps> = observer(({
    email,
    showPhones,
    showPasswords,
    isMissing,
}) => {
    const { t } = useTranslation();
    const {
        currentPhone, passwordInfo, isPasswordSaving, onPhoneSubmit, onPasswordSave,
    } = useRecipientDetails({ email, showPhones, showPasswords });

    const hasPhone = !!(currentPhone?.phone && currentPhone?.prefix);
    const hasPassword = !!passwordInfo?.hasPassword;
    const initial = email.charAt(0).toUpperCase();

    const phoneMissing = showPhones && !hasPhone;
    const passwordMissing = showPasswords && !hasPassword;

    const passwordHintText = appConfig.USE_SIMPLIFIED_PASSWORD_PROMPT
        ? t('recipientPage.OTPSteps.simplifiedPasswordPrompt')
        : t(`${policyStepNameSpace}.socialIdHint`);

    if (isMissing) {
        return (
            <div className={styles['member-row-missing']}>
                <div className={styles['member-left']}>
                    <div className={styles['member-avatar']}>
                        {initial}
                    </div>
                    <span className={styles['member-email']}>{email}</span>
                </div>
                <div className={styles['member-inputs-stacked']}>
                    {showPhones && (
                        phoneMissing
                            ? (
                                <MissingPhoneInput
                                    phone={currentPhone?.phone}
                                    prefix={currentPhone?.prefix}
                                    onSubmit={onPhoneSubmit}
                                    alwaysShowInput
                                />
                            )
                            : (
                                <span className={styles['detail-text-inline']}>
                                    {`+${currentPhone.prefix}*******${currentPhone.phone.slice(PHONE_LAST_DIGITS)}`}
                                </span>
                            )
                    )}
                    {showPasswords && (
                        passwordMissing
                            ? (
                                <MissingPasswordInput
                                    maskedPassword={passwordInfo?.maskedPassword}
                                    onSave={onPasswordSave}
                                    isLoading={isPasswordSaving}
                                    hintText={passwordHintText}
                                    alwaysShowInput
                                />
                            )
                            : (
                                <span className={styles['detail-text-inline']}>
                                    {`•••••••••${(passwordInfo?.maskedPassword || '').slice(PHONE_LAST_DIGITS)}`}
                                </span>
                            )
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles['member-row']}>
            <div className={styles['member-avatar']}>
                {initial}
            </div>
            <span className={styles['member-email']}>{email}</span>
            {showPhones && (
                <span className={styles['detail-text']}>
                    <EditableCellPopup
                        type="phone"
                        phone={currentPhone?.phone}
                        prefix={currentPhone?.prefix}
                        onSave={onPhoneSubmit}
                        isEmpty={!hasPhone}
                        cellTestId={`groupDetailsModal_memberPhone_${email}`}
                        displayValue={
                            hasPhone
                                ? `+${currentPhone.prefix}*******${currentPhone.phone.slice(PHONE_LAST_DIGITS)}`
                                : ''
                        }
                    />
                </span>
            )}
            {showPasswords && (
                <span className={styles['detail-text']}>
                    <EditableCellPopup
                        type="password"
                        maskedPassword={passwordInfo?.maskedPassword}
                        hasPassword={hasPassword}
                        onSave={onPasswordSave}
                        isEmpty={!hasPassword}
                        cellTestId={`groupDetailsModal_memberPassword_${email}`}
                        displayValue={
                            hasPassword
                                ? `•••••••••${(passwordInfo?.maskedPassword || '').slice(PHONE_LAST_DIGITS)}`
                                : ''
                        }
                    />
                </span>
            )}
        </div>
    );
});

export interface GroupDetailsModalProps {
    showPhones: boolean;
    showPasswords: boolean;
}

const GroupDetailsModal: FC<GroupDetailsModalProps> = observer(({
    showPhones,
    showPasswords,
}) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [detailsReady, setDetailsReady] = useState(false);

    const {
        sharedUsersStore: {
            loadingGroupMembersModal,
            getRecipientPasswordInfo,
            checkGroupMembersPasswords,
        },
        usersPhonesStore: {
            setPhoneNumbersGroupId,
            phoneNumbersGroupId,
            usersPhonesMap,
            fetchGroupMembersPhones,
        },
        usersListStore: {
            userGroupsList,
        },
    } = useStores();

    useEffect(() => {
        if (!phoneNumbersGroupId) return;
        let cancelled = false;
        setDetailsReady(false);

        (async () => {
            const promises: Promise<void>[] = [];
            if (showPhones) promises.push(fetchGroupMembersPhones(phoneNumbersGroupId));
            if (showPasswords) promises.push(checkGroupMembersPasswords(phoneNumbersGroupId));
            await Promise.all(promises);
            if (!cancelled) setDetailsReady(true);
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneNumbersGroupId]);

    const group: CustomGroup | undefined = userGroupsList.find(
        (g) => g.groupId === phoneNumbersGroupId,
    );
    const memberEmails = (group?.members || []).map(({ email }) => email);

    const isMemberMissing = (email: string): boolean => {
        if (showPhones) {
            const phoneInfo = usersPhonesMap.get(email);
            const phone = phoneInfo?.phonesVector?.currentPhone;
            if (!(phone?.phone && phone?.prefix)) return true;
        }
        if (showPasswords) {
            const passwordInfo = getRecipientPasswordInfo(email);
            if (passwordInfo.hasPassword === false) return true;
        }
        return false;
    };

    const sortedEmails = useMemo(() => {
        const filtered = searchQuery.trim()
            ? memberEmails.filter((email) => email.toLowerCase().includes(searchQuery.toLowerCase()))
            : memberEmails;

        const missing: string[] = [];
        const filled: string[] = [];
        filtered.forEach((email) => {
            if (isMemberMissing(email)) {
                missing.push(email);
            } else {
                filled.push(email);
            }
        });
        return [...missing, ...filled];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberEmails, searchQuery, usersPhonesMap.size]);

    const closeModal = (): void => {
        setPhoneNumbersGroupId(null);
    };

    const isLoading = !detailsReady || (!memberEmails.length && loadingGroupMembersModal);

    return (
        <Modal
            isOpen
            loading={loadingGroupMembersModal}
            closeModal={closeModal}
            dynamicStyles={MODAL_DYNAMIC_STYLES}
            initStyle={INIT_STYLE}
        >
            <>
                <ModalHeader>
                    <span className={styles.title}>
                        {group?.groupName || ''}
                    </span>
                </ModalHeader>
                <div className={styles['group-members-content']}>
                    <div className={styles['scroll-inner']}>
                        <div className={styles['search-wrapper']}>
                            <Input
                                prefix={<SearchOutlined className={styles['search-icon']} />}
                                placeholder={t(`${nameSpace}.searchMembers`)}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                allowClear
                                className={styles['search-input']}
                            />
                        </div>
                        {isLoading ? (
                            <div className={styles['loader-wrapper']}>
                                <LoadingOutlined className={styles.loader} spin />
                            </div>
                        ) : (
                            <div className={styles['members-list']}>
                                {sortedEmails.map((email) => (
                                    <MemberRow
                                        key={email}
                                        email={email}
                                        showPhones={showPhones}
                                        showPasswords={showPasswords}
                                        isMissing={isMemberMissing(email)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </>
        </Modal>
    );
});

export default GroupDetailsModal;
